// ★ VR — o MÊS do lead e o RESGATE — puro, sem dependências (o script do degrau
// importa este arquivo direto pelo Node).
//
// ORIGEM FIXA: o lead pertence ao mês de `criadoEm` (1º contato — coluna D da
// planilha; nunca muda). RESGATE: uma ação nova — agendou / compareceu / fechou —
// em mês POSTERIOR ao de origem grava esse mês em `resgates: ['AAAA-MM', …]`
// (sem repetição) e `ultimoResgateEm`, NO MESMO LOTE da ação. O lead reaparece
// naquele mês com o selo "resgate", sem deixar de ser NOVO no mês de origem.
//
// O que NÃO realoca (decisão do dono): nota, tentativa, tarefa, "não agendou",
// "não compareceu", "não fechou". Resgate NÃO entra no denominador de taxa
// nenhuma; "Leads do mês" continua sendo só os novos por `criadoEm`.

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** 'AAAA-MM' (mês local) de uma Date/Timestamp/ISO; null se inválido. */
export function mesDe(data) {
  const d = paraData(data);
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : null;
}

/** O mês de resgate de uma ação: só quando é POSTERIOR ao mês de `criadoEm`; senão null. */
export function mesDeResgate(lead, dataAcao) {
  const origem = mesDe(lead?.criadoEm);
  const acao = mesDe(dataAcao);
  if (!origem || !acao) return null;
  return acao > origem ? acao : null;
}

const listaDe = (lead) => (Array.isArray(lead?.resgates) ? lead.resgates.filter((m) => typeof m === 'string') : []);

/**
 * As mudanças a gravar no lead para registrar o resgate desta ação — ou null
 * quando não há nada a gravar (mesma safra, mês anterior, ou mês já registrado).
 * Quem chama espalha o resultado no update do MESMO lote da ação.
 */
export function comResgate(lead, dataAcao) {
  const mes = mesDeResgate(lead, dataAcao);
  if (!mes) return null;
  const atuais = listaDe(lead);
  if (atuais.includes(mes)) return null;
  return { resgates: [...atuais, mes].sort(), ultimoResgateEm: paraData(dataAcao) };
}

/** Como o lead aparece num mês: 'novo' (mês de criadoEm — vence), 'resgate' (mês em resgates) ou null. */
export function aparicoesNoMes(lead, mes) {
  if (!mes) return null;
  if (mesDe(lead?.criadoEm) === mes) return 'novo';
  return listaDe(lead).includes(mes) ? 'resgate' : null;
}

/**
 * Como o lead aparece numa JANELA [inicio, fim] (filtro por 7d/30d/90d/intervalo):
 * 'novo' se `criadoEm` cai na janela; 'resgate' se algum mês de `resgates`
 * intersecta a janela; senão null. Sem janela → 'novo' (todo o período: uma
 * aparição só por lead — nunca duplica).
 */
export function aparicaoNaJanela(lead, janela) {
  if (!janela) return 'novo';
  const criado = paraData(lead?.criadoEm);
  const dentro = (d) => d && (!janela.inicio || d >= janela.inicio) && (!janela.fim || d <= janela.fim);
  if (dentro(criado)) return 'novo';
  for (const m of listaDe(lead)) {
    const [a, mm] = m.split('-').map(Number);
    if (!a || !mm) continue;
    const ini = new Date(a, mm - 1, 1, 0, 0, 0, 0);
    const fim = new Date(a, mm, 0, 23, 59, 59, 999);
    const cruza = (!janela.inicio || fim >= janela.inicio) && (!janela.fim || ini <= janela.fim);
    if (cruza) return 'resgate';
  }
  return null;
}

/** Contagem para o rodapé do filtro: { novos, resgates } de uma lista já filtrada. */
export function contarAparicoes(leads, janela) {
  let novos = 0;
  let resgates = 0;
  for (const l of leads ?? []) {
    const a = aparicaoNaJanela(l, janela);
    if (a === 'novo') novos += 1;
    else if (a === 'resgate') resgates += 1;
  }
  return { novos, resgates };
}

/** Metas/Dashboard: os leads com resgate no mês (linha "Resgates"; NÃO entra em taxa). */
export const leadsComResgateNoMes = (leads, mes) =>
  (leads ?? []).filter((l) => aparicoesNoMes(l, mes) === 'resgate');

// ------------------------------------------------------------ importados (V7)
const MES_DA_ABA = { JANEIRO: '2026-01', FEVEREIRO: '2026-02', 'MARÇO': '2026-03', ABRIL: '2026-04', MAIO: '2026-05', JUNHO: '2026-06', JULHO: '2026-07', AGOSTO: '2026-08', SETEMBRO: '2026-09' };

/**
 * Os resgates de um lead importado, a partir de `importacao.linhas` ('ABA:n'):
 * cada aba de mês POSTERIOR ao mês de `criadoEm`, sem repetir, em ordem. É o que
 * o degrau grava nos 523 leads que a planilha trouxe em mais de um mês.
 */
export function resgatesDaImportacao(lead) {
  const origem = mesDe(lead?.criadoEm);
  const linhas = Array.isArray(lead?.importacao?.linhas) ? lead.importacao.linhas : [];
  if (!origem || linhas.length === 0) return [];
  const meses = new Set();
  for (const ref of linhas) {
    const aba = String(ref ?? '').split(':')[0];
    const mes = MES_DA_ABA[aba];
    if (mes && mes > origem) meses.add(mes);
  }
  return [...meses].sort();
}
